import type { AccountInfo } from '@azure/msal-browser';

/** What a fresh sign-in needs from MSAL, structurally, so it can be tested without a browser. */
export interface FreshSignInClient {
  acquireTokenSilent(request: { scopes: string[]; account: AccountInfo }): Promise<{ accessToken: string }>;
  acquireTokenPopup(request: {
    scopes: string[];
    account: AccountInfo;
    prompt: 'login';
    redirectUri: string;
  }): Promise<{ accessToken: string }>;
}

/**
 * Where the popup returns: the app's redirect-bridge page (`apps/player-web/redirect.html`), registered
 * as a redirect URI per origin on the player registration (docs/entra-setup.md §3.3).
 */
export const REDIRECT_BRIDGE_PATH = '/redirect.html';

/**
 * A token from a sign-in made just now, for erasing an account (ADR 0010 Decision 7, ADR 0012 step 28).
 *
 * **`prompt: 'login'`, never a silent acquisition.** The server checks the token's `auth_time` — when
 * the person last typed a code — and a silent renewal keeps the original one: the staging token of
 * 2026-10-02 was issued 25 minutes after the sign-in it carried. So this always asks.
 *
 * **A popup, not a redirect.** The person is in the middle of a confirmation; a redirect would replace
 * the page and lose it. A closed or blocked popup rejects, and the caller says so — there is nothing to
 * fall back to, since any token not got this way would be refused anyway.
 *
 * **The popup returns to the redirect-bridge page, not to `origin`.** MSAL v5's opener waits on a
 * BroadcastChannel that only `broadcastResponseToMainFrame()` sends on (see `runRedirectBridge`), so a
 * popup returning to the app root boots the whole portal and is never heard — which is what staging did
 * on 2026-10-03. The override is on this request alone. Sign-in (`loginRedirect`), the redirect fallback
 * in `acquireToken` and silent renewal all stay on the root, where redirect flows complete without a
 * bridge; the hidden-iframe renewal does not, and survives only through that redirect fallback — known
 * gap 25 in CLAUDE.md, whose fix is moving the config-wide `redirectUri` to the bridge.
 */
export async function freshSignIn(
  client: FreshSignInClient,
  account: AccountInfo,
  scope: string,
  origin: string,
): Promise<string> {
  const request = { scopes: [scope], account, prompt: 'login' as const, redirectUri: origin + REDIRECT_BRIDGE_PATH };
  return (await client.acquireTokenPopup(request)).accessToken;
}

/**
 * How long the opener waits for the popup to answer: five minutes, where MSAL's default is one.
 *
 * **MSAL starts this clock when it opens the window, not when the person comes back to it.** In
 * msal-browser 5.20.0 `PopupClient.executeCodeFlow` opens the popup (`PopupClient.ts:412`), then waits
 * (`:425` → `waitForPopupResponse`), and `BrowserUtils.waitForBridgeResponse` arms the timer
 * (`BrowserUtils.ts:304`). So it covers typing an address, waiting for an email, finding it and typing
 * the code — sixty seconds is a race the person did not know they were in.
 *
 * **The server's five-minute `auth_time` window does not bound this**, though it looks as if it should.
 * `auth_time` is stamped when the code is accepted, the popup answers straight after, and the deletion
 * is sent straight after that — so the server measures sign-in → DELETE, which is seconds however long
 * the person spent getting there. What bounds this value instead is its cost: under Entra's COOP the
 * opener cannot see the window close, so somebody who closes it waits this long to be told so. Five
 * minutes is room for a slow email and a resend, and short enough that the wait ends while they are
 * still looking at the screen; it is the same number as the server's window by choice, not by rule.
 */
export const FRESH_SIGN_IN_TIMEOUT_MS = 5 * 60_000;

/**
 * The configuration an app needs for `freshSignIn`'s popup. `popupBridgeTimeout` is a config-wide
 * `system` option — no request can carry one — so it governs every popup the instance opens; the
 * erasure's is the only one in the product, and admin-web and room-web open none.
 */
export function withFreshSignIn<T extends { system?: object }>(config: T): T {
  return { ...config, system: { ...config.system, popupBridgeTimeout: FRESH_SIGN_IN_TIMEOUT_MS } };
}
